import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import { api, fmtMoney, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY = { name: "", description: "", price: 0, active: true };

export default function Equipment() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const { data: items = [] } = useQuery({ queryKey: ["equipment-admin"], queryFn: () => api.get("/equipment?include_inactive=true").then((r) => r.data) });

  const save = async () => {
    try {
      if (editing.id) await api.put(`/equipment/${editing.id}`, editing);
      else await api.post("/equipment", editing);
      qc.invalidateQueries({ queryKey: ["equipment-admin"] });
      qc.invalidateQueries({ queryKey: ["equipment"] });
      setEditing(null);
      toast.success("Saved");
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this add-on?")) return;
    await api.delete(`/equipment/${id}`);
    qc.invalidateQueries({ queryKey: ["equipment-admin"] });
    toast.success("Deleted");
  };

  return (
    <div data-testid="admin-equipment">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-display text-3xl text-white mb-1">Equipment Add-ons</h1><p className="text-white/50 text-sm">Flat-price rental extras shown during booking.</p></div>
        <button onClick={() => setEditing({ ...EMPTY })} data-testid="add-equipment-btn" className="btn-gold rounded-full px-5 py-2.5 text-sm font-mono-label uppercase flex items-center gap-2"><Plus size={15} /> Add</button>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        {items.map((e, i) => (
          <div key={e.id} className="bg-[#111] border border-white/10 rounded-2xl p-5 flex justify-between items-start" data-testid={`equipment-${i}`}>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-display text-white">{e.name}</h3>
                {!e.active && <span className="text-[10px] text-white/40 border border-white/15 rounded-full px-2">inactive</span>}
              </div>
              <p className="text-white/40 text-xs mt-1">{e.description}</p>
              <p className="text-[#D4AF37] font-semibold mt-2">+{fmtMoney(Math.round(e.price * 100))}</p>
            </div>
            <div className="flex gap-1">
              <button onClick={() => setEditing(e)} data-testid={`edit-equipment-${i}`} className="text-white/50 hover:text-[#D4AF37] p-1.5"><Pencil size={15} /></button>
              <button onClick={() => remove(e.id)} data-testid={`delete-equipment-${i}`} className="text-white/50 hover:text-red-400 p-1.5"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#111] border-white/15 text-white">
          <DialogHeader><DialogTitle className="font-display">{editing?.id ? "Edit" : "New"} Add-on</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <Input placeholder="Name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="bg-[#0a0a0a] border-white/15" data-testid="equipment-name" />
              <Textarea placeholder="Description" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="bg-[#0a0a0a] border-white/15" />
              <Input type="number" placeholder="Price ($)" value={editing.price} onChange={(e) => setEditing({ ...editing, price: Number(e.target.value) })} className="bg-[#0a0a0a] border-white/15" data-testid="equipment-price" />
              <div className="flex items-center gap-3"><Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} /><span className="text-sm text-white/70">Active</span></div>
              <button onClick={save} data-testid="save-equipment-btn" className="btn-gold w-full rounded-full py-3 text-sm font-mono-label uppercase">Save</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
